import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';

export type FontSize = 'sm' | 'md' | 'lg';

export const FONT_SIZES: readonly FontSize[] = ['sm', 'md', 'lg'];
export const FONT_SIZE_STORAGE_KEY = 'fontSizePreference';
export const DARK_MODE_STORAGE_KEY = 'dark-mode';
export const DARK_THEME_LINK_ID = 'dark-theme';
const DARK_THEME_HREF = 'assets/themes/allianz-one-compact-dark.css';

/**
 * User display preferences (text size, dark mode). Persisted in localStorage and applied to
 * `<html>` as soon as the app boots (see `app.config.ts`), so the login page honours them too.
 *
 * Dark mode = the brand-kit dark theme stylesheet (token overrides on `:root`, appended only
 * while enabled) + the `dark-mode` class driving the app-level `--app-*` variables.
 */
@Injectable({ providedIn: 'root' })
export class AccessibilitySettingsService {
  private readonly document = inject(DOCUMENT);

  readonly fontSize = signal<FontSize>(this.readFontSize());
  readonly darkMode = signal<boolean>(this.readDarkMode());
  readonly canDecreaseFontSize = computed(() => FONT_SIZES.indexOf(this.fontSize()) > 0);
  readonly canIncreaseFontSize = computed(() => FONT_SIZES.indexOf(this.fontSize()) < FONT_SIZES.length - 1);

  /** Applies the stored (or system) preferences to the document. */
  init(): void {
    this.applyFontSize(this.fontSize());
    this.applyDarkMode(this.darkMode());
  }

  decreaseFontSize(): void {
    this.setFontSize(FONT_SIZES[FONT_SIZES.indexOf(this.fontSize()) - 1]);
  }

  increaseFontSize(): void {
    this.setFontSize(FONT_SIZES[FONT_SIZES.indexOf(this.fontSize()) + 1]);
  }

  toggleDarkMode(): void {
    const enabled = !this.darkMode();
    this.darkMode.set(enabled);
    this.applyDarkMode(enabled);
    this.store(DARK_MODE_STORAGE_KEY, JSON.stringify(enabled));
  }

  private setFontSize(size: FontSize | undefined): void {
    if (!size) {
      return;
    }

    this.fontSize.set(size);
    this.applyFontSize(size);
    this.store(FONT_SIZE_STORAGE_KEY, size);
  }

  private applyFontSize(size: FontSize): void {
    const classList = this.document.documentElement.classList;
    FONT_SIZES.forEach((s) => classList.remove(`font-${s}`));
    classList.add(`font-${size}`);
  }

  private applyDarkMode(enabled: boolean): void {
    this.document.documentElement.classList.toggle('dark-mode', enabled);

    const existing = this.document.getElementById(DARK_THEME_LINK_ID);
    if (!enabled) {
      existing?.remove();
      return;
    }
    if (existing) {
      return;
    }

    const link = this.document.createElement('link');
    link.id = DARK_THEME_LINK_ID;
    link.rel = 'stylesheet';
    link.href = DARK_THEME_HREF;
    this.document.head.appendChild(link);
  }

  private readFontSize(): FontSize {
    const stored = this.load(FONT_SIZE_STORAGE_KEY);
    return FONT_SIZES.find((s) => s === stored) ?? 'sm';
  }

  private readDarkMode(): boolean {
    const stored = this.load(DARK_MODE_STORAGE_KEY);
    if (stored === 'true' || stored === 'false') {
      return stored === 'true';
    }

    return this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  }

  private load(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private store(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage can be unavailable (private mode, quota): the preference then just isn't persisted.
    }
  }
}
